/**
 * The backfill ETA in words, for the /looker pill and the CSV. Two modes:
 * when the worker's queue was counted (`lowerBound` false) the figure is
 * "about N"; when it could not be read it stays a lower bound, "at least about
 * N". The other workspaces' job count is never shown, only folded into N
 * (owner ruling 2026-10-13). Literal keys only, so the locale integrity test
 * sees every one.
 *
 * @module services/looker/backfillEtaText
 */
import { etaDisplay, type BackfillEta } from './lookerAppData';

type TFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * "at least about 4 min" / "อย่างน้อยประมาณ 4 นาที" (lower bound), or
 * "about 4 min" / "ประมาณ 4 นาที" (queue counted);
 * minutes null → "ยังประเมินไม่ได้".
 */
export const etaText = (t: TFn, eta: BackfillEta): string => {
  const d = etaDisplay(eta.minutes);
  if (d.unit === 'unknown') return t('looker.backfillStatus.eta.unknown');
  if (eta.lowerBound) {
    return d.unit === 'minutes'
      ? t('looker.backfillStatus.eta.minutes', { n: d.n })
      : t('looker.backfillStatus.eta.hours', { n: d.n });
  }
  return d.unit === 'minutes'
    ? t('looker.backfillStatus.eta.minutesAbout', { n: d.n })
    : t('looker.backfillStatus.eta.hoursAbout', { n: d.n });
};

/** "ETA ≥ 4 min" (lower bound) or "ETA ~4 min" (queue counted) for the CSV's Backfill line; unknown → "ETA unknown", never left out. */
export const csvEtaText = (t: TFn, eta: BackfillEta): string => {
  const d = etaDisplay(eta.minutes);
  if (d.unit === 'unknown') return t('looker.backfillStatus.csvEta.unknown');
  if (eta.lowerBound) {
    return d.unit === 'minutes'
      ? t('looker.backfillStatus.csvEta.minutes', { n: d.n })
      : t('looker.backfillStatus.csvEta.hours', { n: d.n });
  }
  return d.unit === 'minutes'
    ? t('looker.backfillStatus.csvEta.minutesAbout', { n: d.n })
    : t('looker.backfillStatus.csvEta.hoursAbout', { n: d.n });
};
